import {
  CheckCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { round } from '@mahawi/eshop-common/src/round';
import {
  type IWarehouseProductCategorySuggestion,
  type IWarehouseProductSimilarProduct,
  type IWarehouseProductSimilarProductCategory,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Alert,
  Button,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  downloaderSyncWarehouseProduct,
  downloaderWarehouseProductPairWithExistingProduct,
  downloaderWarehouseProductSuggestionsLoad,
} from '../../reducers/downloader/reducer';
import { COLOR, SPACING } from '../../theme/tokens';
import Link from '../link';
import TableComponent from '../table';

enum ESimilarityTagColor {
  HIGH = 'green',
  CANDIDATE = 'gold',
  LOW = 'default',
}

const HIGH_SIMILARITY = 0.9;

type DataType = IWarehouseProductSimilarProduct & { key: string };

const toPercent = (value: number): string => `${round(value * 100, 0)} %`;

const getSimilarityTagColor = ({
  similarity,
  isPairingCandidate,
}: IWarehouseProductSimilarProduct): ESimilarityTagColor => {
  if (similarity >= HIGH_SIMILARITY) {
    return ESimilarityTagColor.HIGH;
  }

  return isPairingCandidate
    ? ESimilarityTagColor.CANDIDATE
    : ESimilarityTagColor.LOW;
};

const matchIcon = (isMatch: boolean): React.ReactElement | null =>
  isMatch ? <CheckCircleOutlined style={{ color: COLOR.success }} /> : null;

function WarehouseProductSuggestions({
  warehouseProductUuid,
  selectedCategoryUUIDs,
  onSelectedCategoryUUIDsChange,
}: {
  warehouseProductUuid: string;
  selectedCategoryUUIDs: string[];
  onSelectedCategoryUUIDsChange: (categoryUUIDs: string[]) => void;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const suggestions = useSelector(
    (s: RootState) => s.Downloader.warehouse.suggestions,
  );

  const load = useCallback((): void => {
    dispatch(downloaderWarehouseProductSuggestionsLoad(warehouseProductUuid));
  }, [dispatch, warehouseProductUuid]);

  useEffect(() => {
    load();
  }, [load]);

  const recommendedCategoryUUIDs = useMemo(
    (): string[] =>
      (suggestions?.suggestedCategories ?? [])
        .filter(
          ({ isRecommended }: IWarehouseProductCategorySuggestion): boolean =>
            isRecommended,
        )
        .map(({ uuid }: IWarehouseProductCategorySuggestion): string => uuid),
    [suggestions?.suggestedCategories],
  );

  const preselectedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      suggestions?.warehouseProductUuid !== warehouseProductUuid ||
      preselectedForRef.current === warehouseProductUuid
    ) {
      return;
    }

    preselectedForRef.current = warehouseProductUuid;

    if (selectedCategoryUUIDs.length === 0) {
      onSelectedCategoryUUIDsChange(recommendedCategoryUUIDs);
    }
  }, [
    suggestions?.warehouseProductUuid,
    warehouseProductUuid,
    recommendedCategoryUUIDs,
    selectedCategoryUUIDs.length,
    onSelectedCategoryUUIDsChange,
  ]);

  const toggleCategory = useCallback(
    (categoryUUID: string, checked: boolean): void => {
      onSelectedCategoryUUIDsChange(
        checked
          ? [...selectedCategoryUUIDs, categoryUUID]
          : selectedCategoryUUIDs.filter(
              (uuid: string): boolean => uuid !== categoryUUID,
            ),
      );
    },
    [onSelectedCategoryUUIDsChange, selectedCategoryUUIDs],
  );

  const columns = useMemo(
    (): ColumnsType<object> => [
      {
        title: 'Name',
        dataIndex: 'name',
        render: (name: string | null, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.uuid}`}
            openInNewTab
            label={name ?? record.uuid}
          />
        ),
        width: '35%',
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        render: (
          brand: string | null,
          record: DataType,
        ): React.ReactElement => (
          <Space size={SPACING.xs}>
            {brand ?? EMPTY_VALUE}
            {matchIcon(record.isBrandMatch)}
          </Space>
        ),
        width: '12%',
      },
      {
        title: 'MPN / GTIN',
        dataIndex: 'mpn',
        render: (_: string | null, record: DataType): React.ReactElement => (
          <Space size={SPACING.none} orientation="vertical">
            <Space size={SPACING.xs}>
              {record.mpn ?? EMPTY_VALUE}
              {matchIcon(record.isMpnMatch)}
            </Space>
            <Space size={SPACING.xs}>
              {record.gtin ?? EMPTY_VALUE}
              {matchIcon(record.isGtinMatch)}
            </Space>
          </Space>
        ),
        width: '18%',
      },
      {
        title: 'Categories',
        dataIndex: 'categories',
        render: (
          categories: IWarehouseProductSimilarProductCategory[],
        ): string =>
          categories
            .map(
              ({ name, uuid }: IWarehouseProductSimilarProductCategory) =>
                name ?? uuid,
            )
            .join(', ') || EMPTY_VALUE,
        width: '20%',
      },
      {
        title: 'Similarity',
        dataIndex: 'similarity',
        render: (similarity: number, record: DataType): React.ReactElement => (
          <Tag color={getSimilarityTagColor(record)}>
            {toPercent(similarity)}
          </Tag>
        ),
        width: '8%',
      },
      {
        title: 'Actions',
        dataIndex: 'uuid',
        render: (productUUID: string): React.ReactElement => (
          <Popconfirm
            placement="bottom"
            title="Are you sure to pair with this product?"
            description="Warehouse product will be paired with this product."
            onConfirm={(): void => {
              dispatch(
                downloaderWarehouseProductPairWithExistingProduct(
                  productUUID,
                  warehouseProductUuid,
                ),
              );
              dispatch(
                downloaderSyncWarehouseProduct(
                  productUUID,
                  warehouseProductUuid,
                ),
              );
            }}
          >
            <Tooltip title="Pair with this product">
              <Button size="small" type="link" icon={<SaveOutlined />} />
            </Tooltip>
          </Popconfirm>
        ),
        width: '7%',
      },
    ],
    [dispatch, warehouseProductUuid],
  );

  const dataSource = useMemo(
    (): DataType[] =>
      (suggestions?.similarProducts ?? []).map(
        (product: IWarehouseProductSimilarProduct): DataType => ({
          ...product,
          key: product.uuid,
        }),
      ),
    [suggestions?.similarProducts],
  );

  const isLoading: boolean = suggestions === undefined;

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Space size={SPACING.lg} align="center">
        <Typography.Title level={4} style={{ margin: 0 }}>
          AI suggestions
        </Typography.Title>
        <Tooltip title="Reload AI suggestions">
          <Button onClick={load} disabled={isLoading}>
            <ReloadOutlined />
          </Button>
        </Tooltip>
        <Spin size="small" spinning={isLoading} />
      </Space>

      {suggestions && !suggestions.isRecentlySynced && (
        <Alert
          type="warning"
          showIcon
          title="Warehouse product was not synced recently, AI suggestions are not generated for it."
        />
      )}

      {suggestions &&
        suggestions.isRecentlySynced &&
        !suggestions.hasEmbedding && (
          <Alert
            type="info"
            showIcon
            title="Embedding for this warehouse product is not available yet."
          />
        )}

      {suggestions && suggestions.suggestedCategories.length > 0 && (
        <Space size={SPACING.sm} wrap>
          <Typography.Text strong>Suggested categories:</Typography.Text>
          {suggestions.suggestedCategories.map(
            ({
              uuid,
              path,
              score,
              supportingProductsCount,
              isRecommended,
            }: IWarehouseProductCategorySuggestion): React.ReactElement => (
              <Tooltip
                key={uuid}
                title={`${isRecommended ? 'Recommended' : 'Optional'} — ${supportingProductsCount} similar products are in this category. Selected categories are assigned when the product is created.`}
              >
                <Tag.CheckableTag
                  checked={selectedCategoryUUIDs.includes(uuid)}
                  onChange={(checked: boolean): void =>
                    toggleCategory(uuid, checked)
                  }
                  style={{ fontWeight: isRecommended ? 600 : 400 }}
                >
                  {path.join(' / ')} ({toPercent(score)})
                </Tag.CheckableTag>
              </Tooltip>
            ),
          )}
          <Button
            size="small"
            type="link"
            disabled={recommendedCategoryUUIDs.length === 0}
            onClick={(): void =>
              onSelectedCategoryUUIDsChange(recommendedCategoryUUIDs)
            }
          >
            Select recommended
          </Button>
          <Button
            size="small"
            type="link"
            disabled={selectedCategoryUUIDs.length === 0}
            onClick={(): void => onSelectedCategoryUUIDsChange([])}
          >
            Clear
          </Button>
        </Space>
      )}

      {suggestions?.hasEmbedding && (
        <TableComponent
          columns={columns}
          dataSource={dataSource}
          isLoading={isLoading}
          size="small"
          emptyText="No similar products found"
        />
      )}
    </Space>
  );
}

export default WarehouseProductSuggestions;
